<template>
  <div>
    <!--TOOLBAR-->
    <tool-bar title="Add Service Log"></tool-bar>
    <!--STUDENT HEADER-->
    <v-container
      class="page-container"
      :class="{'mobile pa-4': isMobile, 'py-4 px-5': isTablet}">
      <v-tabs
        class="student-tabs"
        :class="{'show-arrows-outside': !isMobile && !isTablet}"
        show-arrows
        slot="extension"
        color="transparent"
        hide-slider>
        <v-tab
          v-for="(student, index) in studentsArray"
          :disabled="index !== 0"
          :key="index">
            <span>
              {{ student.studentName }}
              <span @click="onRemoveStudentClicked(student)">
                <v-tooltip :disabled="!showTooltip" top>
                  <v-icon
                    slot="activator"
                    :class="{'white--text': tab === index}"
                    size="16">
                    clear
                  </v-icon>
                  <span>Remove Student</span>
                </v-tooltip>
              </span>
            </span>
        </v-tab>
      </v-tabs>
      <v-alert :value="errorMsg" type="error">
        {{ errorMsg }}
      </v-alert>
      <v-tabs-items v-model="tab">
        <v-tab-item v-for="(student, index) in studentsArray"
                    :key="index">
          <student-card :student="student"></student-card>
          <v-form novalidate>
            <service-log-form
              :student="student"
              :index="index"
              :loadData="loadData"
              :arrayLength="studentsArray.length"
              :absentTypesArray="absentTypesArray"
              :absentReasonsArray="absentReasonsArray"
              :absentAlternativesArray="absentAlternativesArray"
              :groupTypeArray="groupTypeArray"
              :deliveryModeArray="deliveryModeArray"
              :serviceDeliveredArray="serviceDeliveredArray"
              :medicaidArray="medicaidArray"
              :showTooltip="showTooltip"
              :resetValidations="resetValidations"
              v-on:remove:student="onRemoveStudentClicked(student)"
              v-on:form:submitted="submitted = true">
            </service-log-form>
          </v-form>
        </v-tab-item>
      </v-tabs-items>
    </v-container>
    <full-screen-loading-spinner v-if="showLoading"></full-screen-loading-spinner>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import * as moment from 'moment';
import Swal from 'sweetalert2';
import api from '@/utils/api';
import Toolbar from '@/components/toolbar';
import FullScreenLoadingSpinner from '@/components/full-screen-loading-spinner';
import ServiceLogClass from '@/utils/service-log-class';
import * as Constants from '@/constants';
import EventBus from '@/event-bus';
import ServiceLogForm from '@/components/service-log/form/service-log-form';
import StudentCard from '@/components/service-log/form/student-card';

export default {
  /* eslint-disable */
  components: {
    ServiceLogForm,
    Toolbar,
    StudentCard,
    FullScreenLoadingSpinner
  },
  name: 'CreateSL',
  data() {
    return {
      bottomNav: 'create',
      student: null,
      tab: null,
      loadData: false,
      studentsArray: null,
      provider: '',
      slData: '',
      submitted: false,
      skipValidations: false,
      saveOptions: '',
      medicaidArray: '',
      absentTypesArray: '',
      absentReasonsArray: '',
      absentAlternativesArray: '',
      groupTypeArray: '',
      deliveryModeArray: '',
      serviceDeliveredArray: '',
      showLoading: false,
      notes: '',
      errorMsg: '',
      resetValidations: false
    };
  },
  computed: {
    ...mapGetters({
      showTooltip: 'showTooltip'
    }),
    isMobile() {
      return this.$mq === 'mobile';
    },
    isTablet() {
      return this.$mq === 'tablet';
    }
  },
  methods: {
    removeStudent(student) {
      // remove student from the array
      this.studentsArray.splice(this.studentsArray.indexOf(student), 1);
      // remove studentId from localStorage
      // create an array of studentIds
      const newArray = localStorage.getItem('selected-students').split(',').filter((id) => {
        return Number(id) !== (student.serviceId);
      });
      localStorage.setItem('selected-students', newArray);
      EventBus.$emit('reset-validations');
    },
    onRemoveStudentClicked(student) {
      // if there is only one student in the student array, navigate away
      if (this.studentsArray.length === 1) {
        // clear the selected students array
        localStorage.setItem('selected-students', [])
        const subArray = localStorage.getItem('submission-array');
        // navigate away from the page
        if (!subArray || !subArray.length) {
          this.$router.push('/dashboard');
        } else {
          // if there are submissions, take them to the history page
          this.$router.push('/history');
        }
      } else if (this.studentHasChanges(student) && !this.submitted) {
        // there is one more than one student in the array
        // the student being deleted is dirty
        Swal.fire({
          text: 'You have unsaved information.  Are you sure you want to delete this student?',
          confirmButtonText: 'Delete',
          showCancelButton: true,
          customClass: {
            popup: 'popup-class',
            content: 'content-class',
            confirmButton: 'confirm-button-class delete'
          }
        }).then((response) => {
          // if ok was clicked
          if (response && response.value) {
            this.removeStudent(student);
          }
        });
      } else {
        // there are multiple students in the array, but no changes have been made
        this.removeStudent(student);
      }
    },
    hasServiceInformationChanged(info) {
      return !!info.absentType || !!info.groupType || !!info.sessionTime || !!info.sessionTimeEnd || !!info.sessionMinutes || !!info.locationOther || !!info.deliveryMode || !!info.roomName || !!info.notes;
    },
    hasMedicaidInformationChanged(array) {
      let hasChanges = false;
      for (let i = 0, l = array.length; i < l; i++) {
        if (array[i].minutes || array[i].outcome || (array[i].objectives && array[i].objectives.length) || array[i].procedureId || array[i].procedureName) {
          hasChanges = true;
          break;
        }
      }
      return hasChanges;
    },
    studentHasChanges(student) {
      const serviceInformation = student.form.serviceInformation;
      const medicaidInformation = student.form.medicaid;
      return this.hasServiceInformationChanged(serviceInformation) ||
        this.hasMedicaidInformationChanged(medicaidInformation);
    },
    hasChanges() {
      let hasChanges = false;
      const array = this.studentsArray;
      for (let i = 0, l = array.length; i < l; i++) {
        if (this.studentHasChanges(array[i])) {
          hasChanges = true;
          break;
        }
      }
      return hasChanges;
    },
    addFormDataToStudent(student) {
      return new Promise((resolve, reject) => {
        api.get(Constants.GET_SERVICE_LOCATIONS_URL(student.schoolName))
          .then((res) => {
            const locationObj = res.data.data[0];
            // make a copy of the student
            const copy = Object.assign({}, student);
            const data = {
              serviceStartDate: student.startDate,
              serviceEndDate: student.endDate,
              providerFullName: this.provider.firstName + ' ' + this.provider.lastName,
              providerCredential: this.provider.credentialName,
              providerCredentialType: this.provider.credentialType,
              absentType: null,
              absentReason: null,
              absentAlternative: null,
              absentAlternativeDesc: null,
              noServiceNotifFlag: null,
              groupType: null,
              groupSize: null,
              sessionDate: moment().format('YYYY-MM-DD'),
              location: locationObj,
              deliveryMode: null,
              serviceDelivered: null,
              roomName: null,
              providerLocation: null,
              notes: null,
              medicaidSessions: [{
                minutes: null,
                procedureId: null,
                procedureName: null
              }],
              ptSessionBehaviors: [],
              ptSessionProgresses: [{
                id: null,
                task: '',
                measurable: [{
                  ptSessionTaskId: null,
                  type: 'OUT_OF',
                  value1: '',
                  value2: ''
                },
                {
                  ptSessionTaskId: null,
                  type: 'MINUTES',
                  value1: ''
                },
                {
                  ptSessionTaskId: null,
                  type: 'ACCURACY',
                  value1: ''
                }],
                assistance: [{
                  ptSessionTaskId: null,
                  type: 'OUT_OF',
                  value1: '',
                  value2: ''
                },
                {
                  ptSessionTaskId: null,
                  type: 'MINUTES',
                  value1: ''
                },
                {
                  ptSessionTaskId: null,
                  type: 'PERCENT',
                  value1: ''
                },
                {
                  ptSessionTaskId: null,
                  type: 'NONE',
                  value1: ''
                }],
                type: []
              }],
              ptSessionObjectives: [{
                id: null,
                name: '',
                value: ''
              }],
              ptSessionObjectiveImplementation: [[]],
              progressAssessment: '',
              nextSessionFocus: null,
              nextSessionGoal: null,
              sessionNotes: null,
              licenseAttestUserId: null,
              licenseAttestName: null,
              licenseAttestDate: null,
              supervisorName: null,
              supervisorUserId: null,
              supervisorDate: null,
              supervisorCredential: null
            };
            copy.form = new ServiceLogClass(data, false, false);
            resolve(copy);
          })
          .catch((error) => {
            if (error.response && error.response.data && error.response.data.message) {
              reject(error.response.data);
            } else {
              reject(error);
            }
          });
      });
    },
    setProviderInformation(providerId, providersArray) {
      this.provider = providersArray.filter((provider) => {
        return provider.userId.toString() === providerId;
      })[0];
    },
    setStudentInformation(studentsArray) {
      const studentIDArray = localStorage.getItem('selected-students').split(',');
      return studentsArray.filter((student) => {
        return studentIDArray.includes(student.serviceId.toString());
      });
    }
  },
  beforeRouteLeave(to, from, next) {
    // make sure they arent trying to leave the route before checking if information has changed
    if (!this.submitted) {
      // if no information has changed, let them leave
      if (!this.hasChanges()) {
        next();
      } else {
        Swal.fire({
          text: 'You have unsaved information.  Are you sure you want to leave this page?',
          confirmButtonText: 'OK',
          showCancelButton: true,
          customClass: {
            popup: 'popup-class',
            content: 'content-class',
            confirmButton: 'confirm-button-class'
          }
        }).then((response) => {
          // if ok was clicked
          if (response && response.value) {
            next();
          }
        });
      }
    } else {
      // service log is being submitted, allow the page to navigate away
      next();
    }
  },
  mounted() {
    const providerId = localStorage.getItem('provider-id');
    if (providerId) {
      this.showLoading = true;
      Promise.all([
        api.get(Constants.PTSL_PROVIDER_LIST_URL('', providerId)),
        api.get(Constants.PTSL_PROVIDERS_STUDENT_SERVICES_LIST_URL(providerId, '')),
        api.get(Constants.GET_MEDICAID_PROCEDURES_LIST_URL()),
        api.get(Constants.ABSENT_TYPES_URL()),
        api.get(Constants.ABSENT_REASONS_URL()),
        api.get(Constants.ABSENT_ALTERNATIVES_URL()),
        api.get(Constants.GROUP_TYPES_URL()),
        api.get(Constants.DELIVERY_MODES_URL()),
        api.get(Constants.SERVICE_DELIVERED_URL())
      ])
        .then((res) => {
          // set provider information on this.provider
          this.setProviderInformation(providerId, res[0].data.data);
          // set student information
          const students = this.setStudentInformation(res[1].data.data);
          // set medicaidArray
          this.medicaidArray = res[2].data.data;
          // set absentTypes array
          this.absentTypesArray = res[3].data.data;
          // set absentReasons array
          this.absentReasonsArray = res[4].data.data;
          // set absentAlternatives array
          this.absentAlternativesArray = res[5].data.data;
          // set groupTypes array
          this.groupTypeArray = res[6].data.data;
          // set deliveryModeArray
          this.deliveryModeArray = res[7].data.data;
          // set serviceDeliveredArray
          this.serviceDeliveredArray = res[8].data.data;
          const promises = [];
          // create a promise array to add data to each student
          students.forEach((student) => {
            promises.push(this.addFormDataToStudent(student));
          });
          return Promise.all(promises);
        })
        .then((promiseArrays) => {
          this.studentsArray = promiseArrays;
        })
        .catch((error) => {
          if (error.response && error.response.data && error.response.data.message) {
            this.errorMsg = error.response.data.message;
          } else {
            this.errorMsg = error.message;
          }
        })
        .finally(() => {
          this.loadData = true;
          this.showLoading = false;
        });
    } else {
      this.errorMsg = 'Provider ID is missing.';
    }
  }
};
</script>

<style lang="scss" scoped>
  .student-tabs {
    width: 100%;
    height: 58px;
    margin-bottom: -18px;
    /deep/ .v-tabs__div {
      .v-tabs__item {
        margin-bottom: -10px;
        padding-bottom: 24px;
        height: 58px;
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
        margin-right: -1px;

        &.v-tabs__item--active {
          background-color: var(--v-primary-base);
          color: #fff;
          font-weight: bold;
        }

        &.v-tabs__item--disabled {
          border: 2px solid var(--v-primary-base);
          background-color: #D8E2F6;
          color: var(--v-primary-base);
        }
      }
    }
    /deep/ .v-tabs__icon--prev {
      color: var(--v-primary-base);
    }
    /deep/ .v-tabs__icon--next {
      color: var(--v-primary-base);
    }
    &.show-arrows-outside {
      /deep/ .v-tabs__wrapper--show-arrows {
        margin: 0;
      }
      /deep/ .v-tabs__icon--prev {
        left: -32px;
      }
      /deep/ .v-tabs__icon--next {
        right: -32px;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/views/service-log/create-service-log.vue